import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { Button } from '../button';
import { Icon } from '../icon';
import { Warning } from '../icons/warning';
import { Text } from '../text';

export enum AlertType {
  BRAND = 'brand',
  WARNING = 'warning',
  ERROR = 'error',
  SUCCESS = 'success',
}

interface AlertProps {
  type: AlertType;
  title?: string;
  body?: string;
  buttonText?: string;
  onPress?: () => void;
  icon?: React.ReactNode;
  iconWidth?: number;
  iconHeight?: number;
  iconContainerStyle?: any;
}

export default function Alert({
  type,
  title,
  body,
  buttonText,
  onPress,
  icon = <Warning />,
  iconWidth = 20,
  iconHeight = 20,
  iconContainerStyle,
}: AlertProps) {
  const { theme, styles } = useStyles(stylesheet);

  return (
    <View style={[styles.container, styles[`${type}Container`]]}>
      <View style={{ flexDirection: 'row', gap: 16 }}>
        <View style={[iconContainerStyle]}>
          <Icon
            type="naked"
            icon={icon}
            fillColor={styles[`${type}IconColor`].color}
            width={iconWidth}
            height={iconHeight}
          />
        </View>

        <View style={{ flexShrink: 1 }}>
          {title ? <Text type="bodyRegular">{title}</Text> : null}

          {body ? (
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {body}
            </Text>
          ) : null}
        </View>
      </View>

      {buttonText && onPress ? (
        <View>
          <Button
            text={buttonText}
            type="primarySmall"
            onPress={onPress}
            style={[styles.button, styles[`${type}Button`]]}
          />
        </View>
      ) : null}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
    gap: 16,
  },
  brandContainer: {
    backgroundColor: theme.colors.alert.brand.backgroundColor,
    borderColor: theme.colors.alert.brand.border,
  },
  warningContainer: {
    backgroundColor: theme.colors.alert.warning.backgroundColor,
    borderColor: theme.colors.alert.warning.border,
  },
  errorContainer: {
    backgroundColor: theme.colors.alert.error.backgroundColor,
    borderColor: theme.colors.alert.error.border,
  },
  successContainer: {
    backgroundColor: theme.colors.alert.success.backgroundColor,
    borderColor: theme.colors.alert.success.border,
  },
  brandIconColor: {
    color: theme.colors.icon.brandColor,
  },
  warningIconColor: {
    color: theme.colors.icon.attentionColor,
  },
  errorIconColor: {
    color: theme.colors.icon.errorColor,
  },
  successIconColor: {
    color: theme.colors.icon.successColor,
  },
  button: {
    width: 200,
  },
  brandButton: {
    backgroundColor: theme.colors.alert.brand.buttonBackgroundColor,
  },
  warningButton: {
    backgroundColor: theme.colors.alert.warning.buttonBackgroundColor,
  },
  errorButton: {
    backgroundColor: theme.colors.alert.error.buttonBackgroundColor,
  },
  successButton: {
    backgroundColor: theme.colors.alert.success.buttonBackgroundColor,
  },
}));
